import CryptoJS from 'crypto-js'
import { v4 as uuid4 } from 'uuid'
import { AesCBCEncryptV2, buildSignature } from './encryption'
// start_ai_generated
import { isInApp } from '../../browser'
import { encryptV3, getHeaderV3, getNativeInfo } from '../../jsbridge'

interface RandomField {
  unisign: string
  keyindent: string
  once: string
  ts: number
}

interface CredentialHeaders {
  randomField: RandomField
  headers: Record<string, string>
}

interface EcdhKey {
  shareKey: string
  keyIndent: string
}

const buildRandomString = (): string => uuid4().replace(/-/g, '')

const buildRandomField = (): RandomField => ({
  unisign: buildRandomString(),
  keyindent: buildRandomString(),
  once: buildRandomString(),
  ts: Math.round(Date.now() / 1000),
})

const buildBasicHeaders = (): CredentialHeaders => {
  const randomField = buildRandomField()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Replay-Info': 'replay',
    'X-Request-Ts': randomField.ts.toString(),
    'X-Request-Once': randomField.once,
  }
  return { randomField, headers }
}

export const buildCredentialHeaders = (): CredentialHeaders => {
  const { randomField, headers } = buildBasicHeaders()
  return { randomField, headers }
}

export const buildExchangeKeyHeaders = (
  key: string,
  body: string
): CredentialHeaders => {
  const { randomField, headers } = buildCredentialHeaders()
  const signature = buildSignature(key, randomField.ts, randomField.once, body)
  headers['X-Signature'] = signature
  headers['X-Key-Ident'] = randomField.keyindent
  headers['App-Name'] = 'eufy_security'
  return { randomField, headers }
}

const encryptBody = async (body: string, key: string): Promise<string> => {
  if (!body || !key) return body
  return await AesCBCEncryptV2(body, key)
}

export const getEdchKeySession = async (): Promise<any> => {
  try {
    const nativeInfo = await getNativeInfo()
    const domain = nativeInfo?.domain
    const key = btoa(`edch_key_${domain}`)
    return key ? getSession(key) : ''
  } catch (e) {
    return ''
  }
}

export const getSession = (key: string): unknown => {
  let data: unknown
  const rawData = sessionStorage.getItem(key)
  if (rawData) {
    const decData = CryptoJS.enc.Base64.parse(rawData).toString(
      CryptoJS.enc.Utf8
    )
    const decJson = CryptoJS.AES.decrypt(decData, 'aes').toString(
      CryptoJS.enc.Utf8
    )
    data = JSON.parse(decJson)
  }
  return data
}

export const buildV3HeadersH5 = async (
  body: string,
  ecdhKey: EcdhKey
): Promise<CredentialHeaders & { encBody: string }> => {
  const { randomField, headers } = buildCredentialHeaders()
  const encBody = await encryptBody(body, ecdhKey.shareKey)
  const signature = buildSignature(
    ecdhKey.shareKey,
    randomField.ts,
    randomField.once,
    encBody
  )
  headers['X-Encryption-Info'] = 'algo_ecdh'
  headers['X-Signature'] = signature
  headers['X-Key-Ident'] = ecdhKey.keyIndent
  headers['App-Name'] = 'eufy_security'
  return { randomField, headers, encBody }
}

export const buildV3HeadersApp = async (
  body: string
): Promise<Record<string, string>> => {
  const ts = String(Math.round(Date.now() / 1000))
  const once = uuid4().replace(/-/g, '')
  const headers = await getHeaderV3(ts, once, body)
  const parseHeader = headers
  return {
    'Content-Type': 'text/plain',
    'X-Encryption-Info': 'algo_ecdh',
    'X-Replay-Info': 'replay',
    'X-Request-Ts': ts,
    'X-Request-Once': once,
    'Model-type': 'PHONE',
    ...parseHeader,
  }
}

export const requestV3Headers = async (
  config: any
): Promise<{ v3_headers: Record<string, string>; config: any }> => {
  if (!config) return { v3_headers: {}, config }
  let v3_headers: Record<string, string> = {}
  // v3 如果不是在app内
  if (!(await isInApp())) {
    const ecdhKey = await getEdchKeySession()
    if (!ecdhKey) {
      console.log(
        `v3 login failed, please check if the native app version is greater than 4.7.9, and the domain is set correctly.`
      )
      return { v3_headers, config }
    }
    const data = !config.data
      ? ''
      : typeof config.data === 'string'
      ? config.data
      : JSON.stringify(config.data)
    const { headers, encBody } = await buildV3HeadersH5(
      data,
      ecdhKey as EcdhKey
    )
    v3_headers = {
      ...headers,
      'Content-Type': 'text/plain',
    }
    if (data) {
      config['data'] = encBody
    }
  } else {
    // v3 如果是在app内
    const newData = config.data
      ? await encryptV3({ type: 0, data: JSON.stringify(config.data) })
      : ''
    const newHeader = await buildV3HeadersApp(newData)
    // start_ai_generated
    const { token } = await getNativeInfo()
    // start_ai_generated
    v3_headers = {
      'X-Auth-Token': token,
      ...newHeader,
    }
    if (newData) {
      config['data'] = newData
    }
    config = {
      ...config,
      credentials: 'omit',
      body: newData,
    }
  }
  return { v3_headers, config }
}
// end_ai_generated
